import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Auth, signInWithCustomToken, signInWithEmailAndPassword } from '@angular/fire/auth';
import { FormPasswordComponent } from '../../shared/components/form-password/form-password.component';
import { FormButtonComponent } from '../../shared/components/form-button/form-button.component';
import { ApiError } from '../../abstract/api-error';
import { AuthInvitationService } from '../../core/api-services/auth-invitation.service';
import { AuthService } from '../../core/services/auth.service';
import { InvitationFlowType } from '../../core/data-models/invitation-flow-type.model';
import { InvitationLookupResponse } from '../../core/response-models/invitation-lookup-response.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';
import { firebaseErrorMessage } from 'src/app/shared/utils/firebase-error.utils';

/**
 * Accept Invitation Page Component
 *
 * Allows users to accept invitations sent by admins or organization owners.
 * Uses the unified GET /auth/invitations/:token endpoint to validate tokens
 * from both invitation tables (organization and profile) in a single call.
 *
 * Handles three different flows based on user's account status:
 * 1. login_required: User already has Firebase account - prompts to log in
 * 2. password_setup: User profile exists without Firebase - creates Firebase account
 * 3. account_creation: New user - creates profile and Firebase account
 */
@Component({
  selector: 'app-accept-invitation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FormPasswordComponent,
    FormButtonComponent,
    AuthThemeDirective,
    MaxLengthDirective,
  ],
  templateUrl: './accept-invitation.component.html',
  styleUrls: ['./accept-invitation.component.scss']
})
export class AcceptInvitationComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;

  // Forms for different flows
  passwordForm: FormGroup;
  loginForm: FormGroup;

  // State
  loading: boolean = false;
  loadingInvitation: boolean = true;
  errorMessage: string = '';
  successMessage: string = '';
  invitationToken: string = '';
  invitationDetails: InvitationLookupResponse | null = null;
  invitationError: string = '';
  flowType: InvitationFlowType | null = null;

  private auth = inject(Auth);
  private authService = inject(AuthService);
  private authInvitations = inject(AuthInvitationService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Form for password setup / account creation flows
    this.passwordForm = this.fb.group({
      firstName: ['', [Validators.maxLength(100)]],
      lastName: ['', [Validators.maxLength(100)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Form for login_required flow
    this.loginForm = this.fb.group({
      password: ['', [Validators.required]]
    });
  }

  async ngOnInit(): Promise<void> {
    // Get invitation token from query params
    this.invitationToken = this.route.snapshot.queryParams['token'];

    if (!this.invitationToken) {
      this.invitationError = 'Invalid invitation link. No token provided.';
      this.loadingInvitation = false;
      return;
    }

    // Validate invitation and determine flow
    await this.validateInvitation();
  }

  /**
   * Validate invitation token using the unified endpoint.
   * A single call to GET /auth/invitations/:token searches both invitation tables
   * and returns the source type, flow type, and all needed fields.
   */
  async validateInvitation(): Promise<void> {
    this.loadingInvitation = true;
    this.invitationError = '';

    try {
      const details = await this.authInvitations.getInvitation(this.invitationToken);
      this.invitationDetails = details;
      this.flowType = details.flow.type;

      // Pre-fill names from the invitation, falling back to an existing profile
      const { invitation, profile } = details;
      this.passwordForm.patchValue({
        firstName: invitation?.firstName || profile?.firstName || '',
        lastName: invitation?.lastName || profile?.lastName || '',
      });
    } catch (error: unknown) {
      console.error('Validate invitation error:', error);
      this.handleInvitationError(ApiError.fromHttpError(error));
    } finally {
      this.loadingInvitation = false;
    }
  }

  /**
   * Handle invitation validation errors with friendly messages
   */
  private handleInvitationError(error: ApiError): void {
    // Codes from hub `auth-unified-invitation-controller.ts` (lowercase on this endpoint).
    switch (error.code) {
      case 'invalid_token':
        this.invitationError = 'This invitation link is invalid or has already been used. Please contact American Drone Network for assistance.';
        break;
      case 'expired':
        this.invitationError = 'This invitation has expired. Please contact American Drone Network for a new invitation.';
        break;
      case 'cancelled':
        this.invitationError = 'This invitation has been cancelled. Please contact American Drone Network for assistance.';
        break;
      case 'already_accepted':
        this.invitationError = 'This invitation has already been accepted. You can sign in with your account.';
        break;
      default:
        this.invitationError = 'Unable to validate invitation. Please try again or contact American Drone Network for assistance.';
    }
  }

  /**
   * Custom validator to check if passwords match
   */
  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }

    return null;
  }

  /**
   * Handle login form submission (for login_required flow)
   */
  async onLoginSubmit(): Promise<void> {
    if (this.loginForm.invalid || !this.invitationDetails) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { password } = this.loginForm.value;
      const email = this.invitationDetails.profile?.email || this.invitationDetails.invitation.email;
      await signInWithEmailAndPassword(this.auth, email, password);

      // Accept the invitation via the unified endpoint (rejects with an ApiError on failure)
      await this.authInvitations.acceptInvitation(this.invitationToken, { flowType: 'login_required' });
      this.successMessage = 'Invitation accepted! Redirecting to dashboard...';

      // Refresh session to pick up updated custom claims and load organization context.
      // Non-fatal: invitation was already accepted, so redirect even if refresh fails.
      try {
        await this.authService.refreshSession();
      } catch (refreshError) {
        console.error('Session refresh error:', refreshError);
      }
      setTimeout(() => this.router.navigate(['/dashboard']), 1500);
    } catch (error: unknown) {
      console.error('Login error:', error);
      this.errorMessage = this.loginErrorMessage(error);
    } finally {
      this.loading = false;
    }
  }

  /** Display copy for a failed login-and-accept: a Firebase sign-in error or a hub `ApiError`. */
  private loginErrorMessage(error: unknown): string {
    if (error instanceof ApiError) {
      return this.acceptErrorMessage(error);
    }
    return firebaseErrorMessage(error, 'Failed to sign in. Please try again.');
  }

  /**
   * Handle password setup form submission (for password_setup and account_creation flows)
   */
  async onPasswordSubmit(): Promise<void> {
    if (this.passwordForm.invalid || !this.invitationDetails || !this.flowType) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { password, firstName, lastName } = this.passwordForm.value;

      // Accept invitation and create/link Firebase account via unified endpoint
      const response = await this.authInvitations.acceptInvitation(this.invitationToken, {
        password,
        firstName: firstName?.trim() || null,
        lastName: lastName?.trim() || null,
        flowType: this.flowType,
      });

      this.successMessage = 'Account created successfully! Signing you in...';
      await this.signInAfterAccept(response.customToken);
    } catch (error: unknown) {
      console.error('Accept invitation error:', error);
      this.errorMessage = this.acceptErrorMessage(ApiError.fromHttpError(error));
    } finally {
      this.loading = false;
    }
  }

  /** Signs in with the hub's custom token and goes to the dashboard, or falls back to the login page. */
  private async signInAfterAccept(customToken: string | undefined): Promise<void> {
    if (!customToken) {
      setTimeout(() => this.router.navigate(['/login']), 2000);
      return;
    }
    await signInWithCustomToken(this.auth, customToken);

    // Refresh session to pick up updated custom claims and load organization context.
    // Non-fatal: sign-in succeeded, so redirect even if refresh fails.
    try {
      await this.authService.refreshSession();
    } catch (refreshError) {
      console.error('Session refresh after invitation acceptance failed:', refreshError);
    }
    setTimeout(() => this.router.navigate(['/dashboard']), 1500);
  }

  /** Display copy for a failed accept, by the hub's error code (`auth-unified-invitation-controller.ts`). */
  private acceptErrorMessage(error: ApiError): string {
    switch (error.code) {
      case 'invalid_password':
        return error.errors[0] ?? 'Invalid password. Must be at least 8 characters.';
      case 'invalid_token':
        return 'Invitation not found.';
      case 'email_mismatch':
        return error.errors[0] ?? 'This invitation was sent to a different email address.';
      case 'email_exists':
        return 'An account already exists with this email. Please sign in instead.';
      case 'expired':
      case 'cancelled':
      case 'already_accepted':
        return error.errors[0] ?? 'This invitation is no longer valid.';
      default:
        return 'Failed to accept invitation. Please try again.';
    }
  }

  /**
   * Get password mismatch error state
   */
  get passwordMismatch(): boolean {
    return this.passwordForm.hasError('passwordMismatch') &&
           this.passwordForm.get('confirmPassword')?.touched || false;
  }

  /**
   * Get role display name from bitwise role value
   */
  getRoleDisplayName(role: number): string {
    const roles: string[] = [];
    if (role & 4) roles.push('Owner');
    if (role & 8) roles.push('Chief Supervisor');
    if (role & 2) roles.push('Pilot in Command');
    if (role & 1) roles.push('Pilot');
    return roles.join(', ') || 'Member';
  }

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
